import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { colors } from "../ui/colors"; // ⬅️ ajusta la ruta si es necesario
import { BaseDialog } from "./BaseDialog";

import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import DrawIcon from "@mui/icons-material/Draw";
import EditIcon from "@mui/icons-material/Edit";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SaveIcon from "@mui/icons-material/Save";

// Tipos de caligrafía disponibles
const CALLIGRAPHY_FONTS = [
  {
    id: "dancing-script",
    name: "Dancing Script",
    fontFamily: "Dancing Script, cursive",
    webFont: "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&display=swap",
  },
  {
    id: "great-vibes",
    name: "Great Vibes",
    fontFamily: "Great Vibes, cursive",
    webFont: "https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap",
  },
  {
    id: "allura",
    name: "Allura",
    fontFamily: "Allura, cursive",
    webFont: "https://fonts.googleapis.com/css2?family=Allura&display=swap",
  },
  {
    id: "pacifico",
    name: "Pacifico",
    fontFamily: "Pacifico, cursive",
    webFont: "https://fonts.googleapis.com/css2?family=Pacifico&display=swap",
  },
  {
    id: "alex-brush",
    name: "Alex Brush",
    fontFamily: "Alex Brush, cursive",
    webFont: "https://fonts.googleapis.com/css2?family=Alex+Brush&display=swap",
  },
];

function trimCanvasToDataURL(canvas, backgroundColor = "#ffffff", padding = 8) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);

  let top = null,
    left = null,
    right = null,
    bottom = null;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const a = data[(y * width + x) * 4 + 3];
      if (a !== 0) {
        if (top === null) top = y;
        if (left === null || x < left) left = x;
        if (right === null || x > right) right = x;
        if (bottom === null || y > bottom) bottom = y;
      }
    }
  }

  if (top === null) {
    const tmp = document.createElement("canvas");
    tmp.width = width;
    tmp.height = height;
    const tctx = tmp.getContext("2d");
    tctx.fillStyle = backgroundColor;
    tctx.fillRect(0, 0, tmp.width, tmp.height);
    return tmp.toDataURL("image/png");
  }

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const pad = clamp(padding, 0, Math.max(width, height));
  const cropX = clamp(left - pad, 0, width);
  const cropY = clamp(top - pad, 0, height);
  const cropW = clamp(right - left + 1 + pad * 2, 1, width - cropX);
  const cropH = clamp(bottom - top + 1 + pad * 2, 1, height - cropY);

  const tmp = document.createElement("canvas");
  tmp.width = cropW;
  tmp.height = cropH;
  const tctx = tmp.getContext("2d");
  tctx.fillStyle = backgroundColor;
  tctx.fillRect(0, 0, cropW, cropH);
  tctx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
  return tmp.toDataURL("image/png");
}

/** HiDPI setup (retina crispness) */
function setupHiDPI(sigRef, widthCss, heightCss, background = "#ffffff") {
  const canvas = sigRef?.current?.getCanvas?.();
  const pad = sigRef?.current?.getSignaturePad?.();
  if (!canvas || !pad) return;

  const ratio = Math.max(window.devicePixelRatio || 1, 1);

  canvas.width = Math.floor(widthCss * ratio);
  canvas.height = Math.floor(heightCss * ratio);
  canvas.style.width = `${widthCss}px`;
  canvas.style.height = `${heightCss}px`;

  const ctx = canvas.getContext("2d");
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, widthCss, heightCss);

  pad.clear();
}

async function renderTypedToPng(
  width,
  height,
  { text, fontFamily, fontSize, fontColor, fontStyle, fontWeight, background = "#ffffff", padding = 8 }
) {
  const dpr = Math.max(window.devicePixelRatio || 1, 1);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  let fontPx = fontSize;
  ctx.font = `${fontStyle === "italic" ? "italic " : ""}${fontWeight} ${fontPx}px ${fontFamily}`;
  let metrics = ctx.measureText(text);
  const maxWidth = width - 24 * 2;
  if (metrics.width > maxWidth) {
    const scale = maxWidth / metrics.width;
    fontPx = Math.max(16, Math.floor(fontSize * scale));
    ctx.font = `${fontStyle === "italic" ? "italic " : ""}${fontWeight} ${fontPx}px ${fontFamily}`;
  }

  ctx.fillStyle = fontColor;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText(text, width / 2, height / 2);

  return trimCanvasToDataURL(canvas, background, padding);
}

/**
 * @param {{
 *   onSave?: (url: string) => void;
 *   containerSx?: any;
 *   height?: number;
 *   label?: string;
 *   initialValue?: string;
 *   role?: string;
 * }} props
 */

const SignatureBaseComponent = ({
  role,
  containerSx,
  boxSx,
  height = 200,
  minWidth = 320,
  maxWidth = 900,
  label = "Signature required to accept the quote",
  hintLabel = "Sign inside the box or switch to Write to type your name.",
  savedLabel = "Signature saved",
  clearLabel = "Clear",
  saveLabel = "Save signature",
  expandLabel = "Expand",
  closeLabel = "Close",
  disabled = false,
  initialValue = null,
  defaultMode = "draw",
  showModeToggle = true,
  typedPlaceholder = "Type your signature",
  fontFamily = "Dancing Script, cursive",
  fontSize = 32,
  fontColor = "#1a1a1a",
  fontStyle = "normal",
  fontWeight = 400,
  webFontUrl = "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&display=swap",
  penColor = "#1a1a1a",
  backgroundColor = "#ffffff",
  minStrokeWidth = 0.8,
  maxStrokeWidth = 2.8,
  throttle = 16,
  velocityFilterWeight = 0.7,
  exportPadding = 8,
  onSave = () => { },
  onSignedChange = () => { },
  exposeApi,
  enableExpand = true,
  modal = {},
}) => {
  const containerRef = useRef(null);
  const sigCanvasRef = useRef(null);

  const [hasSignature, setHasSignature] = useState(false);
  const [dataUrl, setDataUrl] = useState(initialValue);
  const [canvasWidth, setCanvasWidth] = useState(600);
  const [mode, setMode] = useState(defaultMode === "draw" ? "draw" : "write");
  const [typedText, setTypedText] = useState("");
  const [openExpanded, setOpenExpanded] = useState(false);
  const [selectedFont, setSelectedFont] = useState("dancing-script");

  const modalCanvasRef = useRef(null);
  const modalContainerRef = useRef(null);
  const [modalCanvasWidth, setModalCanvasWidth] = useState(800);

  const isBusiness = role === "business";

  const getCurrentFont = () => {
    return CALLIGRAPHY_FONTS.find((font) => font.id === selectedFont) || CALLIGRAPHY_FONTS[0];
  };

  useEffect(() => {
    const update = () => {
      const el = containerRef.current;
      if (!el) return setCanvasWidth(600);
      const styles = window.getComputedStyle(el);
      const pl = parseFloat(styles.paddingLeft) || 0;
      const pr = parseFloat(styles.paddingRight) || 0;
      const inner = el.clientWidth - pl - pr;
      const effective = Math.max(minWidth, Math.min(inner - 4, maxWidth));
      setCanvasWidth(effective);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [minWidth, maxWidth]);

  useEffect(() => {
    if (mode !== "draw") return;
    setupHiDPI(sigCanvasRef, canvasWidth, height, backgroundColor);
  }, [mode, canvasWidth, height, backgroundColor]);

  useEffect(() => {
    if (initialValue) {
      setDataUrl(initialValue);
      setHasSignature(true);
      onSignedChange(true);
    }
  }, [initialValue, onSignedChange]);

  useEffect(() => {
    CALLIGRAPHY_FONTS.forEach((font) => {
      const id = `signature-webfont-${font.id}`;
      if (!document.getElementById(id)) {
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = font.webFont;
        document.head.appendChild(link);
      }
    });
  }, []);

  useEffect(() => {
    if (typeof exposeApi !== "function") return;
    const currentFont = getCurrentFont();
    exposeApi({
      clear: handleClear,
      isSigned: () => !!dataUrl || hasSignature || !!typedText.trim(),
      loadFromDataUrl: (url) => {
        setDataUrl(url);
        setHasSignature(true);
        onSignedChange(true);
      },
      getPng: async (opts = { trim: true, background: "var(--fx-surface-base, #FFFFFF)", padding: exportPadding }) => {
        if (dataUrl) return dataUrl;
        if (mode === "write") {
          if (!typedText.trim()) return null;
          return renderTypedToPng(canvasWidth, height, {
            text: typedText,
            fontFamily: currentFont.fontFamily,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            background: opts.background || "var(--fx-surface-base, #FFFFFF)",
            padding: opts.padding ?? exportPadding,
          });
        }
        const c = sigCanvasRef.current;
        if (!c || c.isEmpty()) return null;
        return (opts.trim ? c.getTrimmedCanvas() : c.getCanvas()).toDataURL("image/png");
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    exposeApi,
    dataUrl,
    hasSignature,
    typedText,
    mode,
    canvasWidth,
    height,
    selectedFont,
    fontSize,
    fontColor,
    fontStyle,
    fontWeight,
    exportPadding,
  ]);

  useEffect(() => {
    if (!openExpanded) return;
    const modalMin = modal.minWidth || 480;
    const modalMax = modal.maxWidth || 1200;
    const updateModalWidth = () => {
      const el = modalContainerRef.current;
      if (!el) return;
      const styles = window.getComputedStyle(el);
      const pl = parseFloat(styles.paddingLeft) || 0;
      const pr = parseFloat(styles.paddingRight) || 0;
      const inner = el.clientWidth - pl - pr;
      const effective = Math.max(modalMin, Math.min(inner - 4, modalMax));
      setModalCanvasWidth(effective);
    };
    updateModalWidth();
    window.addEventListener("resize", updateModalWidth);
    return () => window.removeEventListener("resize", updateModalWidth);
  }, [openExpanded, modal.minWidth, modal.maxWidth]);

  useEffect(() => {
    if (!openExpanded) return;
    const preload = () => {
      try {
        const sourceUrl = dataUrl
          ? dataUrl
          : sigCanvasRef.current && !sigCanvasRef.current.isEmpty()
            ? sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png")
            : null;

        if (sourceUrl && modalCanvasRef.current) {
          modalCanvasRef.current.fromDataURL(sourceUrl);
        }
        setupHiDPI(modalCanvasRef, modalCanvasWidth, modal.height || Math.max(height, 360), backgroundColor);
      } catch { }
    };
    const t = setTimeout(preload, 50);
    return () => clearTimeout(t);
  }, [openExpanded, dataUrl, modalCanvasWidth, modal.height, height, backgroundColor]);

  const handleEnd = () => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      setHasSignature(true);
      if (dataUrl) setDataUrl(null);
      onSignedChange(true);
    }
  };

  const handleClear = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
    if (modalCanvasRef.current) {
      modalCanvasRef.current.clear();
    }
    setHasSignature(false);
    setDataUrl(null);
    setTypedText("");
    onSave(null);
  };

  const handleSave = async () => {
    const currentFont = getCurrentFont();
    if (mode === "write") {
      if (!typedText.trim()) return;
      const url = await renderTypedToPng(canvasWidth, height, {
        text: typedText,
        fontFamily: currentFont.fontFamily,
        fontSize,
        fontColor,
        fontStyle,
        fontWeight,
        background: "var(--fx-surface-base, #FFFFFF)",
        padding: exportPadding,
      });
      setDataUrl(url);
      setHasSignature(true);
      onSave(url);
      return;
    }
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) return;
    const url = sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
    setDataUrl(url);
    setHasSignature(true);
    onSave(url);
  };

  const handleModeChange = (e, val) => {
    if (!val || val === mode) return;
    setMode(val);
    handleClear();
  };

  const openModal = () => setOpenExpanded(true);

  const handleModalClear = () => {
    if (modalCanvasRef.current) modalCanvasRef.current.clear();
  };

  const handleModalApply = () => {
    if (!modalCanvasRef.current || modalCanvasRef.current.isEmpty()) return;
    const url = modalCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
    setDataUrl(url);
    setHasSignature(true);
    onSignedChange(true);
    onSave(url);
    setOpenExpanded(false);
  };

  const handleFontChange = (event) => {
    setSelectedFont(event.target.value);
    if (dataUrl) setDataUrl(null);
  };

  const currentFont = getCurrentFont();

  if (isBusiness) {
    return null;
  }

  return (
    <Box sx={{ ...containerSx }}>
      {/* Título con acento */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 4,
              height: 24,
              bgcolor: colors.orange.base,
              borderRadius: 2,
              mr: 1.5,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.gray[800], ml: 1 }}>
            {label}
          </Typography>
          {dataUrl && (
            <Chip
              icon={<CheckIcon />}
              label="Signed"
              size="small"
              sx={{
                ml: 1.5,
                color: colors.success[700],
                border: `1px solid ${colors.success[300]}`,
                bgcolor: colors.success[50],
                "& .MuiChip-icon": { color: colors.success[600] },
              }}
              variant="outlined"
            />
          )}
        </Box>

        {showModeToggle && !dataUrl && (
          <ToggleButtonGroup
            size="small"
            value={mode}
            exclusive
            onChange={handleModeChange}
            sx={{
              "& .MuiToggleButton-root": {
                border: `1px solid ${colors.gray[200]}`,
                borderRadius: 1,
                color: colors.gray[700],
                backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: colors.orange[50],
                  borderColor: colors.orange[200],
                },
                "&.Mui-selected": {
                  backgroundColor: colors.orange.base,
                  borderColor: colors.orange.base,
                  color: "#fff",
                  "&:hover": { backgroundColor: colors.orange[600] },
                },
              },
            }}
          >
            <ToggleButton value="write" sx={{ px: 2 }}>
              <EditIcon sx={{ mr: 1, fontSize: 16 }} />
              Type
            </ToggleButton>
            <ToggleButton value="draw" sx={{ px: 2 }}>
              <DrawIcon sx={{ mr: 1, fontSize: 16 }} />
              Draw
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Selector de caligrafía */}
      {mode === "write" && !dataUrl && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Calligraphy Style</InputLabel>
            <Select
              value={selectedFont}
              onChange={handleFontChange}
              label="Calligraphy Style"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            >
              {CALLIGRAPHY_FONTS.map((font) => (
                <MenuItem key={font.id} value={font.id}>
                  <Box sx={{ display: "flex", flexDirection: "column", py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.gray[800] }}>
                      {font.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: font.fontFamily,
                        fontSize: "18px",
                        color: colors.gray[600],
                        fontStyle: "italic",
                      }}
                    >
                      Sample signature
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Card
        ref={containerRef}
        elevation={0}
        sx={{
          border: dataUrl
            ? `2px solid ${colors.success.base}`
            : hasSignature
              ? `2px solid ${colors.orange.base}`
              : `2px dashed ${colors.orange.base}`,
          borderRadius: 2,
          backgroundColor: dataUrl ? colors.success[50] : "var(--fx-surface-base, #FFFFFF)",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "visible",
          "&:hover": {
            borderColor: dataUrl ? colors.success[600] : colors.orange[600],
            backgroundColor: dataUrl ? colors.success[50] : colors.orange[50],
          },
          ...boxSx,
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          {dataUrl ? (
            <Fade in>
              <Box>
                <Box
                  component="img"
                  src={dataUrl}
                  alt="Saved signature"
                  sx={{
                    width: "100%",
                    maxWidth: `${canvasWidth}`,
                    height,
                    objectFit: "contain",
                    borderRadius: 1,
                    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                    border: `1px solid ${colors.gray[200]}`,
                    mx: "auto",
                    display: "block",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 1,
                    gap: 1,
                  }}
                >
                  <CheckIcon sx={{ color: colors.success[700], fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: colors.success[700], fontWeight: 600 }}>
                    {savedLabel}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setDataUrl(null);
                      handleClear();
                    }}
                    sx={{ ml: 1, textTransform: "none", color: colors.orange.base, fontWeight: 600 }}
                  >
                    Re-sign
                  </Button>
                </Box>
              </Box>
            </Fade>
          ) : mode === "draw" ? (
            <Box>
              <SignatureCanvas
                ref={sigCanvasRef}
                penColor={penColor}
                backgroundColor={backgroundColor}
                minWidth={minStrokeWidth}
                maxWidth={maxStrokeWidth}
                throttle={throttle}
                velocityFilterWeight={velocityFilterWeight}
                onEnd={handleEnd}
                canvasProps={{
                  width: canvasWidth,
                  height,
                  style: {
                    width: `${canvasWidth}px`,
                    height: `${height}px`,
                    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                    borderRadius: "8px",
                    cursor: "crosshair",
                    display: "block",
                    margin: "0 auto",
                    border: `2px dashed ${colors.orange.base}`,
                  },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mt: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="caption" sx={{ color: colors.gray[600], fontStyle: "italic" }}>
                  {hintLabel}
                </Typography>
                {enableExpand && (
                  <>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: colors.gray[200] }} />
                    <Tooltip title="Open in full screen">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={openModal}
                          disabled={disabled}
                          startIcon={<OpenInFullIcon />}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            borderColor: colors.gray[200],
                            color: colors.gray[700],
                            "&:hover": {
                              borderColor: colors.orange.base,
                              backgroundColor: colors.orange[50],
                            },
                          }}
                        >
                          {expandLabel}
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  width: `${canvasWidth}px`,
                  height,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                  borderRadius: 1,
                  border: `1px solid ${colors.gray[200]}`,
                  mx: "auto",
                  mb: 2,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {typedText ? (
                  <Typography
                    sx={{
                      fontFamily: currentFont.fontFamily,
                      fontSize: `${fontSize}px`,
                      color: fontColor,
                      fontStyle,
                      fontWeight,
                      textAlign: "center",
                      px: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                    }}
                  >
                    {typedText}
                  </Typography>
                ) : (
                  <Typography sx={{ color: colors.gray[400], fontStyle: "italic", fontSize: "16px" }}>Your signature will appear here</Typography>
                )}
              </Box>

              <TextField
                value={typedText}
                onChange={(e) => {
                  const v = e.target.value;
                  setTypedText(v);
                  const signed = !!v.trim();
                  onSignedChange(signed);
                  setHasSignature(signed);
                  if (dataUrl) setDataUrl(null);
                }}
                placeholder={typedPlaceholder}
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "& fieldset": { borderColor: colors.gray[200] },
                    "&:hover fieldset": { borderColor: colors.orange[200] },
                    "&.Mui-focused fieldset": { borderColor: colors.orange.base },
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2, flexWrap: "wrap" }}>
        {!dataUrl && (
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={disabled}
            startIcon={<ClearIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 100,
              borderColor: colors.gray[200],
              color: colors.gray[700],
              "&:hover": { borderColor: colors.orange.base, backgroundColor: colors.orange[50] },
            }}
          >
            {clearLabel}
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={disabled || (!hasSignature && !typedText.trim()) || !!dataUrl}
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            minWidth: 140,
            backgroundColor: colors.orange.base,
            "&:hover": { backgroundColor: colors.orange[600] },
          }}
        >
          {saveLabel}
        </Button>

        {!dataUrl && enableExpand && mode === "draw" && (
          <Tooltip title="Open in full screen">
            <span>
              <IconButton
                onClick={openModal}
                disabled={disabled}
                sx={{
                  border: `1px solid ${colors.gray[200]}`,
                  borderRadius: 2,
                  color: colors.gray[700],
                  "&:hover": { backgroundColor: colors.orange[50], borderColor: colors.orange.base },
                  ml: 0.5,
                }}
              >
                <OpenInFullIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {enableExpand && (
        <BaseDialog
          openState={openExpanded}
          setOpenState={setOpenExpanded}
          title={modal.title || "Draw your signature"}
          closeIcon
          maxWidth="xl"
          hideBackdrop={false}
          PaperProps={{
            sx: {
              maxWidth: "100%",
              width: modal.width || "min(90vw, 1200px)",
              ...(modal.paperSx || {}),
            },
          }}
          content={
            <Box sx={{ p: 1 }}>
              <Box
                ref={modalContainerRef}
                sx={{
                  p: 1,
                  border: `1px solid ${colors.orange[200]}`,
                  bgcolor: colors.orange[50],
                  borderRadius: "8px",
                }}
              >
                <SignatureCanvas
                  ref={modalCanvasRef}
                  penColor={penColor}
                  backgroundColor={backgroundColor}
                  minWidth={minStrokeWidth}
                  maxWidth={maxStrokeWidth}
                  throttle={throttle}
                  velocityFilterWeight={velocityFilterWeight}
                  canvasProps={{
                    width: modalCanvasWidth,
                    height: modal.height || Math.max(height, 360),
                    style: {
                      width: `${modalCanvasWidth}px`,
                      height: `${modal.height || Math.max(height, 360)}px`,
                      border: `2px dashed ${colors.orange.base}`,
                      borderRadius: "8px",
                      backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                      display: "block",
                      margin: "0 auto",
                    },
                  }}
                />
                {hintLabel && (
                  <Typography variant="caption" sx={{ display: "block", mt: 1, color: colors.gray[600], fontStyle: "italic" }}>
                    {hintLabel}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleModalClear}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    borderColor: colors.gray[200],
                    color: colors.gray[700],
                    "&:hover": { borderColor: colors.orange.base, backgroundColor: colors.orange[50] },
                  }}
                >
                  {clearLabel}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={handleModalApply}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    backgroundColor: colors.orange.base,
                    "&:hover": { backgroundColor: colors.orange[600] },
                  }}
                >
                  Apply
                </Button>
                <Button
                  variant="text"
                  startIcon={<CloseIcon />}
                  onClick={() => setOpenExpanded(false)}
                  sx={{ textTransform: "none", color: colors.gray[700], fontWeight: 600 }}
                >
                  {closeLabel}
                </Button>
              </Box>
            </Box>
          }
        />
      )}
    </Box>
  );
};

export default SignatureBaseComponent;
